import { GripVertical, MoreHorizontal, PanelRightOpen, Plus, Trash2, TriangleAlert } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { DateTimeField } from '../DateTimeField';
import { MenuItem, Popover, usePopover } from '../Popover';
import { PeoplePicker, type PersonOption } from '../PersonPicker';
import type { SelectOption } from '../Select';
import { Select } from '../Select';
import { TagInput } from '../TagInput';
import { formatDateTime } from '../../lib/format';
import styles from './EditableTable.module.css';

// QoL §14 (R3): the one editable table — JOB-13's screening criteria and scoring matrix now,
// the interview planner (R7) and import column matching (R4) later. Its keyboard model is
// Tomo's sheet's (CLAUDE.md §6: Enter or F2 edits, Enter saves and moves down, Tab saves and
// moves right, Esc cancels, a click elsewhere saves), written fresh on our components because
// Tomo's grid is bound to its own record model.
//
// Each cell saves on its own and optimistically (CLAUDE.md §5a rules 1 and 4): `onCommit`
// receives the edited row, the caller shows it at once and resolves when the server has it.
// The cell flashes when saved; a failed save flashes it and the caller rolls the row back with
// a toast. Nothing else on screen moves.
//
// R7 adds the planner's kinds: several people (avatars), a date and time in a zone, and a cell
// whose view and editor the screen draws itself (the planner's "Where"). A save the server
// warns about — a double booking — shows its warning in the cell before anything is saved
// (QoL §14): `onCommit` throws a `CellWarning`, and "Save anyway" commits again, confirmed.

/** Thrown by `onCommit` when the server warns instead of saving; the cell shows the words. */
export class CellWarning extends Error {
  constructor(readonly warnings: readonly string[]) {
    super(warnings.join(' '));
    this.name = 'CellWarning';
  }
}

/** What a custom cell's editor is given: the row, and how to finish. */
export interface CustomEditorProps<Row> {
  readonly row: Row;
  readonly label: string;
  /** `next` is null when nothing changed or the edit was cancelled. */
  readonly onDone: (next: Row | null) => void;
}

export type CellSpec<Row> =
  | {
      readonly kind: 'text';
      value(row: Row): string;
      update(row: Row, value: string): Row;
      readonly placeholder?: string;
      readonly maxLength?: number;
    }
  | {
      readonly kind: 'number';
      value(row: Row): number | null;
      update(row: Row, value: number | null): Row;
      /** The whole numbers this row accepts; null where the column doesn't apply, shown as `—`. */
      range(row: Row): { readonly min: number; readonly max: number } | null;
    }
  | {
      readonly kind: 'select';
      value(row: Row): string;
      update(row: Row, value: string): Row;
      readonly options: readonly SelectOption[];
    }
  | {
      readonly kind: 'tags';
      value(row: Row): readonly string[];
      update(row: Row, value: readonly string[]): Row;
      readonly placeholder?: string;
      readonly maxItems?: number;
    }
  | {
      readonly kind: 'checkbox';
      value(row: Row): boolean;
      update(row: Row, value: boolean): Row;
    }
  | {
      /** Read-only: worked out, never typed (a criterion's weight, O24). */
      readonly kind: 'display';
      render(row: Row): ReactNode;
    }
  | {
      /** Several people, with avatars; saved once when the list closes. */
      readonly kind: 'people';
      value(row: Row): readonly string[];
      update(row: Row, value: readonly string[]): Row;
      readonly people: readonly PersonOption[];
      readonly placeholder?: string;
      /** Opt-in: how the chosen people look at rest (PLN-01's pills); the avatars and names otherwise. */
      render?(row: Row): ReactNode;
    }
  | {
      /** A day and a time on `timeZone`'s clock, shown with the zone (non-negotiable #8). */
      readonly kind: 'datetime';
      value(row: Row): string | null;
      update(row: Row, iso: string): Row;
      readonly timeZone: string;
      readonly placeholder?: string;
    }
  | {
      /** The screen draws the cell and its editor; Enter or a click opens the editor. */
      readonly kind: 'custom';
      render(row: Row): ReactNode;
      editor(props: CustomEditorProps<Row>): ReactNode;
    };

export interface EditableColumn<Row> {
  readonly key: string;
  readonly header: string;
  /** A share of the table's width, e.g. `'24%'`; the rest is shared out. */
  readonly width?: string;
  readonly cell: CellSpec<Row>;
  /** False where this row's cell can't change (a finished interview); it reads as display. */
  isEditable?(row: Row): boolean;
  /**
   * Opt-in: true where this row's cell needs a person's attention before the form can go on
   * (import's name picker while no column is the name, Redesign Group 3). It reads red.
   */
  isInvalid?(row: Row): boolean;
}

export interface CellChange<Row> {
  readonly rowKey: string;
  readonly columnKey: string;
  readonly row: Row;
  /** True after "Save anyway" on a warning (QoL §14). */
  readonly isConfirmed: boolean;
}

interface EditableTableProps<Row> {
  readonly label: string;
  readonly columns: readonly EditableColumn<Row>[];
  readonly rows: readonly Row[];
  rowKey(row: Row): string;
  /** How a row is named in its menu and to a screen reader ("Delete JavaScript"). */
  rowLabel(row: Row): string;
  /** Resolves once saved; rejects when the save failed (the caller rolls back and says why). */
  onCommit(change: CellChange<Row>): Promise<void>;
  readonly onAdd?: () => void;
  readonly addLabel?: string;
  readonly isAddDisabled?: boolean;
  readonly onDelete?: (rowKey: string) => void;
  /** QoL §14 / PL-7: a row opens a side panel (its history now). Offered in the row's ⋯ menu. */
  readonly onOpen?: (rowKey: string) => void;
  /** Rows can be reordered: drag the handle, or Alt+↑/↓. */
  readonly onMove?: (from: number, to: number) => void;
  /** More actions in the row's ⋯ menu, above Delete — "Save to library" on a criterion (ADR 0048). */
  readonly rowActions?: (row: Row) => readonly RowAction[];
  readonly emptyText?: string;
  readonly isReadOnly?: boolean;
  /** Opt-in: a saved cell also says "Saved" for its flash (PLN-01, Job-contact.dc.html); other tables only flash. */
  readonly hasSavedWord?: boolean;
}

export interface RowAction {
  readonly label: string;
  readonly icon: ReactNode;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
}

type CellStatus = 'saving' | 'saved' | 'failed';

interface Position {
  readonly row: number;
  readonly col: number;
}

/** A save the server warned about: kept, unsaved, until "Save anyway" or Cancel. */
interface PendingWarning<Row> {
  readonly position: Position;
  readonly row: Row;
  readonly warnings: readonly string[];
}

const FLASH_MS = 900;

export function EditableTable<Row>({
  label,
  columns,
  rows,
  rowKey,
  rowLabel,
  onCommit,
  onAdd,
  addLabel = 'Add row',
  isAddDisabled = false,
  onDelete,
  onOpen,
  rowActions,
  onMove,
  emptyText = 'Nothing here yet',
  isReadOnly = false,
  hasSavedWord = false,
}: EditableTableProps<Row>): ReactNode {
  const [focus, setFocus] = useState<Position | null>(null);
  const [editing, setEditing] = useState<(Position & { readonly initial?: string }) | null>(null);
  const [statuses, setStatuses] = useState<ReadonlyMap<string, CellStatus>>(new Map());
  const [warnings, setWarnings] = useState<ReadonlyMap<string, PendingWarning<Row>>>(new Map());
  const [drag, setDrag] = useState<{ readonly from: number; readonly to: number } | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((timer) => clearTimeout(timer));
  }, []);

  const cellElement = useCallback((position: Position): HTMLElement | null => {
    return tableRef.current?.querySelector<HTMLElement>(`[data-cell="${position.row}:${position.col}"]`) ?? null;
  }, []);

  // The focused cell is the one tab stop into the grid (roving tabindex), and holds focus.
  useEffect(() => {
    if (!focus || editing) return;
    cellElement(focus)?.focus({ preventScroll: false });
  }, [focus, editing, cellElement]);

  const setStatus = useCallback((id: string, status: CellStatus | null) => {
    setStatuses((current) => {
      const next = new Map(current);
      if (status === null) next.delete(id);
      else next.set(id, status);
      return next;
    });
  }, []);

  const setWarning = useCallback((id: string, warning: PendingWarning<Row> | null) => {
    setWarnings((current) => {
      const next = new Map(current);
      if (warning === null) next.delete(id);
      else next.set(id, warning);
      return next;
    });
  }, []);

  const commit = useCallback(
    async (position: Position, row: Row, isConfirmed = false): Promise<void> => {
      const column = columns[position.col];
      const original = rows[position.row];
      if (!column || original === undefined) return;
      const id = `${rowKey(original)}:${column.key}`;
      const previousTimer = timers.current.get(id);
      if (previousTimer) clearTimeout(previousTimer);
      setWarning(id, null);
      setStatus(id, 'saving');
      try {
        await onCommit({ rowKey: rowKey(original), columnKey: column.key, row, isConfirmed });
        setStatus(id, 'saved');
      } catch (error) {
        if (error instanceof CellWarning) {
          // Nothing was saved: the warning waits in the cell for "Save anyway" or Cancel.
          setStatus(id, null);
          setWarning(id, { position, row, warnings: error.warnings });
          return;
        }
        setStatus(id, 'failed');
      }
      timers.current.set(
        id,
        setTimeout(() => setStatus(id, null), FLASH_MS),
      );
    },
    [columns, rows, rowKey, onCommit, setStatus, setWarning],
  );

  const clamp = useCallback(
    (position: Position): Position => ({
      row: Math.max(0, Math.min(position.row, rows.length - 1)),
      col: Math.max(0, Math.min(position.col, columns.length - 1)),
    }),
    [rows.length, columns.length],
  );

  function finishEdit(position: Position, row: Row | null, advance: 'down' | 'right' | 'left' | null): void {
    setEditing(null);
    if (row !== null) void commit(position, row);
    const next =
      advance === 'down'
        ? { row: position.row + 1, col: position.col }
        : advance === 'right'
          ? { row: position.row, col: position.col + 1 }
          : advance === 'left'
            ? { row: position.row, col: position.col - 1 }
            : position;
    setFocus(clamp(next));
  }

  function startEdit(position: Position, initial?: string): void {
    if (isReadOnly) return;
    const column = columns[position.col];
    const row = rows[position.row];
    if (!column || row === undefined) return;
    const { cell } = column;
    if (cell.kind === 'display') return;
    if (column.isEditable && !column.isEditable(row)) return;
    if (cell.kind === 'number' && cell.range(row) === null) return;
    if (cell.kind === 'checkbox') {
      void commit(position, cell.update(row, !cell.value(row)));
      return;
    }
    if (cell.kind === 'select' || cell.kind === 'people') {
      // A select's own button opens its anchored list (CLAUDE.md §5).
      cellElement(position)?.querySelector<HTMLButtonElement>('button')?.click();
      return;
    }
    setEditing({ ...position, ...(initial !== undefined ? { initial } : {}) });
  }

  function onGridKeyDown(event: KeyboardEvent<HTMLTableElement>): void {
    if (editing || !focus) return;
    const move = (rowDelta: number, colDelta: number): void => {
      event.preventDefault();
      setFocus(clamp({ row: focus.row + rowDelta, col: focus.col + colDelta }));
    };
    if (event.altKey && onMove && !isReadOnly && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      event.preventDefault();
      const to = focus.row + (event.key === 'ArrowUp' ? -1 : 1);
      if (to < 0 || to >= rows.length) return;
      onMove(focus.row, to);
      setFocus({ row: to, col: focus.col });
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        return move(1, 0);
      case 'ArrowUp':
        return move(-1, 0);
      case 'ArrowLeft':
        return move(0, -1);
      case 'ArrowRight':
        return move(0, 1);
      case 'Enter':
      case 'F2':
      case ' ':
        event.preventDefault();
        startEdit(focus);
        return;
      default: {
        // Typing over a text or number cell starts editing it with that character.
        const column = columns[focus.col];
        const isTyping = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
        if (isTyping && (column?.cell.kind === 'text' || column?.cell.kind === 'number')) {
          event.preventDefault();
          startEdit(focus, event.key);
        }
      }
    }
  }

  // --- Drag to reorder (D22 `ranking`), copied from the board's pointer drag ---------------

  function startDrag(event: ReactPointerEvent<HTMLButtonElement>, from: number): void {
    if (!onMove || isReadOnly) return;
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    setDrag({ from, to: from });
    const rowsInBody = Array.from(tableRef.current?.tBodies[0]?.rows ?? []);
    const targetAt = (clientY: number): number => {
      const index = rowsInBody.findIndex((element) => {
        const box = element.getBoundingClientRect();
        return clientY < box.top + box.height / 2;
      });
      return index === -1 ? rowsInBody.length - 1 : index;
    };
    const onPointerMove = (moveEvent: PointerEvent): void => setDrag({ from, to: targetAt(moveEvent.clientY) });
    const onPointerUp = (upEvent: PointerEvent): void => {
      handle.removeEventListener('pointermove', onPointerMove);
      handle.removeEventListener('pointerup', onPointerUp);
      handle.removeEventListener('pointercancel', onPointerUp);
      const to = targetAt(upEvent.clientY);
      setDrag(null);
      if (upEvent.type === 'pointerup' && to !== from) onMove(from, to);
    };
    handle.addEventListener('pointermove', onPointerMove);
    handle.addEventListener('pointerup', onPointerUp);
    handle.addEventListener('pointercancel', onPointerUp);
  }

  const canDelete = onDelete !== undefined && !isReadOnly;
  const hasMenu = canDelete || onOpen !== undefined || rowActions !== undefined;
  const hasHandle = onMove !== undefined && !isReadOnly;

  return (
    <div className={styles.wrap}>
      <table
        ref={tableRef}
        className={styles.table}
        role="grid"
        aria-label={label}
        aria-readonly={isReadOnly || undefined}
        onKeyDown={onGridKeyDown}
      >
        <colgroup>
          {hasHandle && <col className={styles.handleCol} />}
          {columns.map((column) => (
            <col key={column.key} {...(column.width ? { style: { width: column.width } } : {})} />
          ))}
          {hasMenu && <col className={styles.menuCol} />}
        </colgroup>
        <thead className={styles.head}>
          <tr>
            {hasHandle && <th aria-label="Order" />}
            {columns.map((column) => (
              <th key={column.key} scope="col">
                {column.header}
              </th>
            ))}
            {hasMenu && <th aria-label="Row actions" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const key = rowKey(row);
            const isDragged = drag?.from === rowIndex;
            const isDropTarget = drag !== null && drag.to === rowIndex && drag.from !== rowIndex;
            return (
              <tr
                key={key}
                className={styles.row}
                data-dragged={isDragged || undefined}
                data-drop={isDropTarget ? (drag.to > drag.from ? 'below' : 'above') : undefined}
              >
                {hasHandle && (
                  <td className={styles.handleCell}>
                    <button
                      type="button"
                      className={styles.handle}
                      aria-label={`Move ${rowLabel(row)}. Alt and the arrow keys move it too`}
                      onPointerDown={(event) => startDrag(event, rowIndex)}
                      onKeyDown={(event) => {
                        if (!event.altKey || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') || !onMove) return;
                        event.preventDefault();
                        event.stopPropagation();
                        const to = rowIndex + (event.key === 'ArrowUp' ? -1 : 1);
                        if (to >= 0 && to < rows.length) onMove(rowIndex, to);
                      }}
                    >
                      <GripVertical size={16} aria-hidden />
                    </button>
                  </td>
                )}
                {columns.map((column, colIndex) => {
                  const position = { row: rowIndex, col: colIndex };
                  const isFocused = focus?.row === rowIndex && focus.col === colIndex;
                  const isEditing = editing?.row === rowIndex && editing.col === colIndex;
                  const isFirstStop = focus === null && rowIndex === 0 && colIndex === 0;
                  const isCellReadOnly = isReadOnly || (column.isEditable !== undefined && !column.isEditable(row));
                  const warning = warnings.get(`${key}:${column.key}`);
                  const status = statuses.get(`${key}:${column.key}`);
                  const isInvalid = column.isInvalid?.(row) === true;
                  return (
                    <td
                      key={column.key}
                      data-cell={`${rowIndex}:${colIndex}`}
                      data-label={column.header}
                      data-status={warning ? 'warning' : status}
                      data-kind={isCellReadOnly && column.cell.kind !== 'display' ? 'readonly' : column.cell.kind}
                      data-invalid={isInvalid || undefined}
                      aria-invalid={isInvalid || undefined}
                      className={styles.cell}
                      role="gridcell"
                      tabIndex={isFocused || isFirstStop ? 0 : -1}
                      aria-selected={isFocused}
                      onFocus={() => {
                        if (!isFocused) setFocus(position);
                      }}
                      onClick={() => {
                        setFocus(position);
                        if (!isEditing) startEdit(position);
                      }}
                    >
                      {isEditing ? (
                        <CellEditor
                          cell={column.cell}
                          row={row}
                          initial={editing?.initial}
                          label={`${column.header} for ${rowLabel(row)}`}
                          onDone={(next, advance) => finishEdit(position, next, advance)}
                        />
                      ) : (
                        <CellView
                          cell={column.cell}
                          row={row}
                          label={`${column.header} for ${rowLabel(row)}`}
                          isReadOnly={isCellReadOnly}
                          isInvalid={isInvalid}
                          onPick={(next) => void commit(position, next)}
                        />
                      )}
                      {hasSavedWord && status === 'saved' && !isEditing && (
                        <span className={styles.savedWord} role="status">
                          Saved
                        </span>
                      )}
                      {warning && (
                        <CellWarningBox
                          warnings={warning.warnings}
                          onAccept={() => void commit(warning.position, warning.row, true)}
                          onCancel={() => setWarning(`${key}:${column.key}`, null)}
                        />
                      )}
                    </td>
                  );
                })}
                {hasMenu && (
                  <td className={styles.menuCell}>
                    <RowMenu
                      label={rowLabel(row)}
                      {...(onOpen ? { onOpen: () => onOpen(key) } : {})}
                      {...(rowActions ? { actions: rowActions(row) } : {})}
                      {...(canDelete && onDelete ? { onDelete: () => onDelete(key) } : {})}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && <p className={styles.empty}>{emptyText}</p>}
      {onAdd && !isReadOnly && (
        <div className={styles.addRow}>
          <Button variant="ghost" size="sm" icon={Plus} onClick={onAdd} disabled={isAddDisabled}>
            {addLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

// --- A cell at rest -----------------------------------------------------------------------

function CellView<Row>({
  cell,
  row,
  label,
  isReadOnly,
  isInvalid,
  onPick,
}: {
  readonly cell: CellSpec<Row>;
  readonly row: Row;
  readonly label: string;
  readonly isReadOnly: boolean;
  readonly isInvalid: boolean;
  readonly onPick: (next: Row) => void;
}): ReactNode {
  switch (cell.kind) {
    case 'text': {
      const value = cell.value(row);
      return value ? <span className={styles.text}>{value}</span> : <span className={styles.placeholder}>{cell.placeholder ?? '—'}</span>;
    }
    case 'number': {
      if (cell.range(row) === null) return <span className={styles.muted}>—</span>;
      const value = cell.value(row);
      return <span className={styles.number}>{value ?? '—'}</span>;
    }
    case 'select':
      // Stops the cell's own click from re-opening the list it just closed.
      return (
        <span className={styles.selectWrap} onClick={(event) => event.stopPropagation()}>
          <Select
            label={label}
            value={cell.value(row)}
            options={cell.options}
            disabled={isReadOnly}
            isInvalid={isInvalid}
            onChange={(value) => {
              if (value !== cell.value(row)) onPick(cell.update(row, value));
            }}
          />
        </span>
      );
    case 'tags': {
      const value = cell.value(row);
      // A cell that can't be typed in has no prompt to show: empty reads "—" (06-qol §11).
      if (value.length === 0) return isReadOnly ? <span className={styles.muted}>—</span> : <span className={styles.placeholder}>{cell.placeholder ?? '—'}</span>;
      return (
        <span className={styles.tags}>
          {value.map((tag) => (
            <span key={tag} className={styles.tag}>
              {tag}
            </span>
          ))}
        </span>
      );
    }
    case 'checkbox':
      return (
        <span className={styles.check} onClick={(event) => event.stopPropagation()}>
          <Checkbox checked={cell.value(row)} ariaLabel={label} disabled={isReadOnly} onChange={(checked) => onPick(cell.update(row, checked))} />
        </span>
      );
    case 'display':
      return <span className={styles.display}>{cell.render(row)}</span>;
    case 'people':
      return (
        <span className={styles.selectWrap} onClick={(event) => event.stopPropagation()}>
          <PeoplePicker
            label={label}
            people={cell.people}
            value={cell.value(row)}
            placeholder={cell.placeholder ?? 'Choose people'}
            isRequired
            disabled={isReadOnly}
            onChange={(value) => onPick(cell.update(row, value))}
            {...(cell.render ? { valueContent: cell.render(row) } : {})}
          />
        </span>
      );
    case 'datetime': {
      const value = cell.value(row);
      return value ? (
        <span className={styles.text}>{formatDateTime(value, { timeZone: cell.timeZone })}</span>
      ) : (
        <span className={styles.placeholder}>{cell.placeholder ?? '—'}</span>
      );
    }
    case 'custom':
      return <span className={styles.display}>{cell.render(row)}</span>;
  }
}

/** QoL §14: the server's warning, in the cell, before anything is saved. */
function CellWarningBox({ warnings, onAccept, onCancel }: { readonly warnings: readonly string[]; readonly onAccept: () => void; readonly onCancel: () => void }): ReactNode {
  return (
    <div
      className={styles.warning}
      role="alert"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape') onCancel();
      }}
    >
      <span className={styles.warningText}>
        <TriangleAlert size={14} aria-hidden />
        {warnings.join(' ')} Not saved yet.
      </span>
      <span className={styles.warningActions}>
        <Button size="sm" variant="solid" onClick={onAccept} autoFocus>
          Save anyway
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </span>
    </div>
  );
}

// --- A cell being edited ---------------------------------------------------------------------

type Advance = 'down' | 'right' | 'left' | null;

function CellEditor<Row>({
  cell,
  row,
  initial,
  label,
  onDone,
}: {
  readonly cell: CellSpec<Row>;
  readonly row: Row;
  readonly initial: string | undefined;
  readonly label: string;
  /** `next` is null when nothing changed or the edit was cancelled. */
  readonly onDone: (next: Row | null, advance: Advance) => void;
}): ReactNode {
  if (cell.kind === 'text' || cell.kind === 'number') {
    return <TextEditor cell={cell} row={row} initial={initial} label={label} onDone={onDone} />;
  }
  if (cell.kind === 'tags') return <TagsEditor cell={cell} row={row} label={label} onDone={onDone} />;
  if (cell.kind === 'datetime') return <DateTimeEditor cell={cell} row={row} label={label} onDone={onDone} />;
  if (cell.kind === 'custom') return <CustomEditor cell={cell} row={row} label={label} onDone={onDone} />;
  return null;
}

/**
 * The day and the time, then Set (or Enter); Esc cancels. No "click outside saves" here: the
 * day and time lists open in anchored popovers outside the cell, so a click in them is not a
 * click away.
 */
function DateTimeEditor<Row>({
  cell,
  row,
  label,
  onDone,
}: {
  readonly cell: Extract<CellSpec<Row>, { kind: 'datetime' }>;
  readonly row: Row;
  readonly label: string;
  readonly onDone: (next: Row | null, advance: Advance) => void;
}): ReactNode {
  const stored = cell.value(row);
  const [draft, setDraft] = useState<string | null>(stored);

  function finish(advance: Advance, isCancelled = false): void {
    onDone(isCancelled || draft === null || draft === stored ? null : cell.update(row, draft), advance);
  }

  return (
    <div
      className={styles.inlineEditor}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape') {
          event.preventDefault();
          finish(null, true);
        } else if (event.key === 'Enter' && event.target instanceof HTMLElement && event.target.dataset.set !== undefined) {
          event.preventDefault();
          finish('down');
        }
      }}
    >
      <DateTimeField label={label} value={stored} timeZone={cell.timeZone} onChange={setDraft} />
      <span className={styles.inlineActions}>
        <Button size="sm" variant="solid" data-set onClick={() => finish(null)} disabled={draft === null || draft === stored}>
          Set
        </Button>
        <Button size="sm" variant="ghost" onClick={() => finish(null, true)}>
          Cancel
        </Button>
      </span>
    </div>
  );
}

function CustomEditor<Row>({
  cell,
  row,
  label,
  onDone,
}: {
  readonly cell: Extract<CellSpec<Row>, { kind: 'custom' }>;
  readonly row: Row;
  readonly label: string;
  readonly onDone: (next: Row | null, advance: Advance) => void;
}): ReactNode {
  return (
    <div
      className={styles.inlineEditor}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Escape') {
          event.preventDefault();
          onDone(null, null);
        }
      }}
    >
      {cell.editor({ row, label, onDone: (next) => onDone(next, null) })}
    </div>
  );
}

function TextEditor<Row>({
  cell,
  row,
  initial,
  label,
  onDone,
}: {
  readonly cell: Extract<CellSpec<Row>, { kind: 'text' | 'number' }>;
  readonly row: Row;
  readonly initial: string | undefined;
  readonly label: string;
  readonly onDone: (next: Row | null, advance: Advance) => void;
}): ReactNode {
  const stored = cell.kind === 'text' ? cell.value(row) : (cell.value(row)?.toString() ?? '');
  const [draft, setDraft] = useState(initial ?? stored);
  const doneRef = useRef(false);

  function finish(advance: Advance, isCancelled = false): void {
    if (doneRef.current) return;
    doneRef.current = true;
    if (isCancelled || draft === stored) {
      onDone(null, advance);
      return;
    }
    if (cell.kind === 'text') {
      onDone(cell.update(row, draft), advance);
      return;
    }
    const trimmed = draft.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    const range = cell.range(row);
    if (parsed !== null && (!Number.isInteger(parsed) || range === null || parsed < range.min || parsed > range.max)) {
      // Out of range: the old value stays; nothing is saved that the server would refuse.
      onDone(null, advance);
      return;
    }
    onDone(cell.update(row, parsed), advance);
  }

  return (
    <input
      autoFocus
      className={styles.editor}
      aria-label={label}
      value={draft}
      inputMode={cell.kind === 'number' ? 'numeric' : 'text'}
      maxLength={cell.kind === 'text' ? cell.maxLength : undefined}
      onChange={(event) => setDraft(event.target.value)}
      onClick={(event) => event.stopPropagation()}
      onBlur={() => finish(null)}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === 'Enter') {
          event.preventDefault();
          finish('down');
        } else if (event.key === 'Tab') {
          event.preventDefault();
          finish(event.shiftKey ? 'left' : 'right');
        } else if (event.key === 'Escape') {
          event.preventDefault();
          finish(null, true);
        }
      }}
    />
  );
}

function TagsEditor<Row>({
  cell,
  row,
  label,
  onDone,
}: {
  readonly cell: Extract<CellSpec<Row>, { kind: 'tags' }>;
  readonly row: Row;
  readonly label: string;
  readonly onDone: (next: Row | null, advance: Advance) => void;
}): ReactNode {
  const stored = cell.value(row);
  const [draft, setDraft] = useState<readonly string[]>(stored);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const doneRef = useRef(false);
  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const finish = useCallback(
    (advance: Advance, isCancelled = false): void => {
      if (doneRef.current) return;
      doneRef.current = true;
      const next = draftRef.current;
      const isSame = next.length === stored.length && next.every((tag, index) => tag === stored[index]);
      onDone(isCancelled || isSame ? null : cell.update(row, next), advance);
    },
    [cell, row, stored, onDone],
  );

  // A click outside saves, as in a spreadsheet.
  useEffect(() => {
    const onPointerDown = (event: PointerEvent): void => {
      if (event.target instanceof Node && wrapRef.current?.contains(event.target)) return;
      finish(null);
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => window.removeEventListener('pointerdown', onPointerDown, true);
  }, [finish]);

  useEffect(() => {
    wrapRef.current?.querySelector('input')?.focus();
  }, []);

  return (
    <div
      ref={wrapRef}
      className={styles.tagsEditor}
      aria-label={label}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        // Enter adds a word (TagInput's own); Tab saves and moves on, Esc cancels.
        event.stopPropagation();
        if (event.key === 'Tab') {
          event.preventDefault();
          finish(event.shiftKey ? 'left' : 'right');
        } else if (event.key === 'Escape') {
          event.preventDefault();
          finish(null, true);
        }
      }}
    >
      <TagInput value={draft} onChange={setDraft} placeholder={cell.placeholder} maxItems={cell.maxItems} />
    </div>
  );
}

// --- A row's ⋯ menu (QoL §14: open its side panel; delete, with undo) -----------------------

function RowMenu({
  label,
  onOpen,
  actions = [],
  onDelete,
}: {
  readonly label: string;
  readonly onOpen?: () => void;
  readonly actions?: readonly RowAction[];
  readonly onDelete?: () => void;
}): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  return (
    <>
      <button ref={triggerRef} type="button" className={styles.menuButton} aria-label={`More for ${label}`} onClick={toggle}>
        <MoreHorizontal size={16} aria-hidden />
      </button>
      <Popover anchor={anchor} onClose={close} label={`Actions for ${label}`} align="right">
        {onOpen && (
          <MenuItem
            icon={<PanelRightOpen size={15} />}
            onClick={() => {
              close();
              onOpen();
            }}
          >
            Open
          </MenuItem>
        )}
        {actions.map((action) => (
          <MenuItem
            key={action.label}
            icon={action.icon}
            disabled={action.disabled ?? false}
            onClick={() => {
              close();
              action.onSelect();
            }}
          >
            {action.label}
          </MenuItem>
        ))}
        {onDelete && (
          <MenuItem
            icon={<Trash2 size={15} />}
            isDanger
            onClick={() => {
              close();
              onDelete();
            }}
          >
            Delete
          </MenuItem>
        )}
      </Popover>
    </>
  );
}
